import React, { Component } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';

let resolveConfirmation = null;
let setConfirmationState = null;

/**
 * Show an accessible confirmation dialog. Returns a Promise that resolves to true (confirmed) or false (cancelled).
 * Usage: let confirmed = await showConfirmation("Are you sure you want to delete this item?");
 */
export function showConfirmation(message, options = {}) {
    return new Promise((resolve) => {
        resolveConfirmation = resolve;
        if (setConfirmationState) {
            setConfirmationState({
                isOpen: true,
                message: message,
                confirmText: options.confirmText || 'Yes',
                cancelText: options.cancelText || 'Cancel',
                title: options.title || 'Confirm Action',
                isPrompt: false,
                promptValue: ''
            });
        } else {
            resolve(false);
        }
    });
}

/**
 * Show an accessible prompt dialog with a text input. Returns a Promise that resolves to
 * { confirmed: true, value: "user input" } or { confirmed: false, value: "" }.
 * Usage: let result = await showPrompt("Version Comments:");
 */
export function showPrompt(message, options = {}) {
    return new Promise((resolve) => {
        resolveConfirmation = resolve;
        if (setConfirmationState) {
            setConfirmationState({
                isOpen: true,
                message: message,
                confirmText: options.confirmText || 'OK',
                cancelText: options.cancelText || 'Cancel',
                title: options.title || 'Input Required',
                isPrompt: true,
                promptValue: options.defaultValue || ''
            });
        } else {
            resolve({ confirmed: false, value: '' });
        }
    });
}

export class ConfirmationModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isOpen: false,
            message: '',
            confirmText: 'Yes',
            cancelText: 'Cancel',
            title: 'Confirm Action',
            isPrompt: false,
            promptValue: ''
        };
        setConfirmationState = (newState) => this.setState(newState);
    }

    componentWillUnmount() {
        setConfirmationState = null;
    }

    handleConfirm = () => {
        let isPrompt = this.state.isPrompt;
        let promptValue = this.state.promptValue;
        this.setState({ isOpen: false, promptValue: '' });
        if (resolveConfirmation) {
            if (isPrompt) {
                resolveConfirmation({ confirmed: true, value: promptValue });
            } else {
                resolveConfirmation(true);
            }
            resolveConfirmation = null;
        }
    }

    handleCancel = () => {
        let isPrompt = this.state.isPrompt;
        this.setState({ isOpen: false, promptValue: '' });
        if (resolveConfirmation) {
            if (isPrompt) {
                resolveConfirmation({ confirmed: false, value: '' });
            } else {
                resolveConfirmation(false);
            }
            resolveConfirmation = null;
        }
    }

    handlePromptChange = (e) => {
        this.setState({ promptValue: e.target.value });
    }

    handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            this.handleConfirm();
        }
    }

    render() {
        let promptInput = null;
        if (this.state.isPrompt) {
            promptInput = (
                <div style={{ marginTop: '10px' }}>
                    <label htmlFor="confirmation-modal-prompt" className="sr-only">
                        {this.state.message}
                    </label>
                    <input
                        type="text"
                        id="confirmation-modal-prompt"
                        className="form-control"
                        value={this.state.promptValue}
                        onChange={this.handlePromptChange}
                        onKeyDown={this.handleKeyDown}
                        autoFocus
                    />
                </div>
            );
        }

        return (
            <Modal
                isOpen={this.state.isOpen}
                toggle={this.handleCancel}
                role="alertdialog"
                aria-labelledby="confirmation-modal-title"
                aria-describedby="confirmation-modal-message"
            >
                <ModalHeader
                    toggle={this.handleCancel}
                    id="confirmation-modal-title"
                >
                    {this.state.title}
                </ModalHeader>
                <ModalBody>
                    <p id="confirmation-modal-message">{this.state.message}</p>
                    {promptInput}
                </ModalBody>
                <ModalFooter>
                    <Button color="danger" onClick={this.handleConfirm}>
                        {this.state.confirmText}
                    </Button>{' '}
                    <Button color="secondary" onClick={this.handleCancel}>
                        {this.state.cancelText}
                    </Button>
                </ModalFooter>
            </Modal>
        );
    }
}
